import React, { useEffect, useRef, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormHelperText,
	FormLabel,
	Link,
	Radio,
	RadioGroup,
	Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import TranscriptField from "./TranscriptField";
import FirefliesInterviewPicker from "./FirefliesInterviewPicker";
import {
	GrammaticalGender,
	InterviewKind,
	InterviewMatchMethod,
	InterviewRecording,
	InterviewTranscript,
	PresentationDraft
} from "../../../models/PresentationDraft";
import { LanguageCode } from "../../../models/enums/LanguageCode";
import {
	useImportInterviewTranscript,
	useInterviewRecordings,
	useStartPresentationDraft
} from "../../../hooks/usePresentationDrafts";
import { useSnackbarContext } from "../../../contexts/useSnackBarContext";
import { zoullooColors } from "../../../theme";
import {
	MAX_TRANSCRIPT_LENGTH,
	MIN_TRANSCRIPT_LENGTH,
	isTranscriptLongEnough,
	toInterviewPayload
} from "../../../utils/presentationDraft";

interface StartPresentationDraftDialogProps {
	readonly open: boolean;
	readonly onClose: () => void;
	readonly freelancerProfileId: number;
	readonly talentName: string;
	readonly talentLanguage: LanguageCode;
	readonly onStarted: (draft: PresentationDraft) => void;
}

const StartPresentationDraftDialog: React.FC<StartPresentationDraftDialogProps> = ({
	open,
	onClose,
	freelancerProfileId,
	talentName,
	talentLanguage,
	onStarted
}) => {
	const { t } = useTranslation();
	const { showError } = useSnackbarContext();
	const startDraft = useStartPresentationDraft();

	const [secondInterview, setSecondInterview] = useState("");
	const [firstInterview, setFirstInterview] = useState("");
	const [showFirstInterview, setShowFirstInterview] = useState(false);
	const [gender, setGender] = useState<GrammaticalGender | "">("");
	const [submitted, setSubmitted] = useState(false);

	const interviews = useInterviewRecordings(freelancerProfileId, open);
	const importSecond = useImportInterviewTranscript();
	const importFirst = useImportInterviewTranscript();
	const [selectedSecondId, setSelectedSecondId] = useState<string>();
	const [selectedFirstId, setSelectedFirstId] = useState<string>();
	const [importedSecond, setImportedSecond] = useState<InterviewTranscript>();
	const [importedFirst, setImportedFirst] = useState<InterviewTranscript>();
	const hasAutoImported = useRef(false);

	const secondInterviews = interviews.data?.filter(interview => interview.kind === InterviewKind.SecondInterview) ?? [];
	const firstInterviews = interviews.data?.filter(interview => interview.kind === InterviewKind.FirstInterview) ?? [];
	const isImporting = importSecond.isPending || importFirst.isPending;

	const importInterview = (
		interview: InterviewRecording,
		importer: ReturnType<typeof useImportInterviewTranscript>,
		setTranscript: React.Dispatch<React.SetStateAction<string>>,
		setSelectedId: (id: string) => void,
		setImported: (transcript: InterviewTranscript) => void,
		keepTypedText: boolean
	) => {
		setSelectedId(interview.id);
		importer.mutate(
			{ freelancerProfileId, recordingId: interview.id },
			{
				onSuccess: transcript => {
					setImported(transcript);
					setTranscript(current => (keepTypedText && current.trim() ? current : transcript.text));
				},
				onError: () => showError("presentationDraft.start.fireflies.importFailed")
			}
		);
	};

	useEffect(() => {
		if (!interviews.data || hasAutoImported.current) return;
		hasAutoImported.current = true;

		if (secondInterviews[0]) {
			importInterview(secondInterviews[0], importSecond, setSecondInterview, setSelectedSecondId, setImportedSecond, true);
		}
		if (firstInterviews[0]) {
			setShowFirstInterview(true);
			importInterview(firstInterviews[0], importFirst, setFirstInterview, setSelectedFirstId, setImportedFirst, true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [interviews.data]);

	const renderFirefliesStatus = () => {
		if (interviews.isLoading) {
			return (
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<CircularProgress size={16} color="secondary" />
					<Typography variant="body2" color="text.secondary">
						{t("presentationDraft.start.fireflies.searching")}
					</Typography>
				</Box>
			);
		}
		if (interviews.isError) {
			return (
				<Typography variant="body2" color="text.secondary">
					{t("presentationDraft.start.fireflies.unavailable")}
				</Typography>
			);
		}
		if (interviews.data && secondInterviews.length === 0 && firstInterviews.length === 0) {
			return (
				<Typography variant="body2" color="text.secondary">
					{t("presentationDraft.start.fireflies.noneFound")}
				</Typography>
			);
		}
		return null;
	};

	const secondInterviewError = (() => {
		if (!submitted) return undefined;
		if (!isTranscriptLongEnough(secondInterview)) {
			return t("presentationDraft.start.errors.transcriptTooShort", { min: MIN_TRANSCRIPT_LENGTH });
		}
		if (secondInterview.length > MAX_TRANSCRIPT_LENGTH) {
			return t("presentationDraft.start.errors.transcriptTooLong", { max: MAX_TRANSCRIPT_LENGTH });
		}
		return undefined;
	})();

	const firstInterviewError =
		submitted && firstInterview.length > MAX_TRANSCRIPT_LENGTH
			? t("presentationDraft.start.errors.transcriptTooLong", { max: MAX_TRANSCRIPT_LENGTH })
			: undefined;

	const genderError = submitted && !gender ? t("presentationDraft.start.errors.genderRequired") : undefined;

	const handleClose = () => {
		if (startDraft.isPending) return;
		onClose();
	};

	const handleSubmit = () => {
		setSubmitted(true);

		const isValid =
			isTranscriptLongEnough(secondInterview) &&
			secondInterview.length <= MAX_TRANSCRIPT_LENGTH &&
			firstInterview.length <= MAX_TRANSCRIPT_LENGTH &&
			!!gender;

		if (!isValid || !gender) return;

		const second = toInterviewPayload(secondInterview, importedSecond);
		const first = toInterviewPayload(firstInterview, importedFirst);

		startDraft.mutate(
			{
				freelancerProfileId,
				secondInterviewTranscriptId: second.transcriptId,
				secondInterviewTranscript: second.text,
				firstInterviewTranscriptId: first.transcriptId,
				firstInterviewTranscript: first.text,
				gender
			},
			{ onSuccess: onStarted }
		);
	};

	const languageLabel = t(
		talentLanguage === LanguageCode.En ? "presentationDraft.languages.en" : "presentationDraft.languages.fr"
	);

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
			<DialogTitle>{t("presentationDraft.start.title")}</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3, pt: "8px !important" }}>
				<Typography variant="body2" color="text.secondary">
					{t("presentationDraft.start.hint", { name: talentName })}
				</Typography>

				{renderFirefliesStatus()}

				{secondInterviews.length > 0 && (
					<FirefliesInterviewPicker
						interviews={secondInterviews}
						selectedId={selectedSecondId}
						isImporting={importSecond.isPending}
						matchedByName={importedSecond?.matchMethod === InterviewMatchMethod.Name}
						onSelect={interview =>
							importInterview(
								interview,
								importSecond,
								setSecondInterview,
								setSelectedSecondId,
								setImportedSecond,
								false
							)
						}
					/>
				)}

				<TranscriptField
					label={t("presentationDraft.start.secondInterview")}
					value={secondInterview}
					onChange={setSecondInterview}
					required
					error={secondInterviewError}
					onFileError={key => showError(key)}
				/>

				<Box>
					<Link component="button" type="button" variant="body2" onClick={() => setShowFirstInterview(value => !value)}>
						{t("presentationDraft.start.firstInterviewToggle")}
					</Link>
					<Collapse in={showFirstInterview} unmountOnExit>
						<Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
							{firstInterviews.length > 0 && (
								<FirefliesInterviewPicker
									interviews={firstInterviews}
									selectedId={selectedFirstId}
									isImporting={importFirst.isPending}
									matchedByName={importedFirst?.matchMethod === InterviewMatchMethod.Name}
									onSelect={interview =>
										importInterview(
											interview,
											importFirst,
											setFirstInterview,
											setSelectedFirstId,
											setImportedFirst,
											false
										)
									}
								/>
							)}
							<TranscriptField
								label={t("presentationDraft.start.firstInterview")}
								value={firstInterview}
								onChange={setFirstInterview}
								error={firstInterviewError}
								onFileError={key => showError(key)}
							/>
						</Box>
					</Collapse>
				</Box>

				<FormControl error={!!genderError} required>
					<FormLabel id="presentation-draft-gender">{t("presentationDraft.start.gender")}</FormLabel>
					<RadioGroup
						row
						aria-labelledby="presentation-draft-gender"
						value={gender}
						onChange={event => setGender(event.target.value as GrammaticalGender)}
					>
						<FormControlLabel
							value={GrammaticalGender.Feminine}
							control={<Radio />}
							label={t("presentationDraft.start.feminine")}
						/>
						<FormControlLabel
							value={GrammaticalGender.Masculine}
							control={<Radio />}
							label={t("presentationDraft.start.masculine")}
						/>
					</RadioGroup>
					<FormHelperText>{genderError ?? t("presentationDraft.start.genderHelp")}</FormHelperText>
				</FormControl>

				<Box sx={{ backgroundColor: zoullooColors.grey[100], borderRadius: "8px", px: 2, py: 1.25, alignSelf: "flex-start" }}>
					<Typography variant="body2" color="text.secondary">
						{t("presentationDraft.start.language", { language: languageLabel })}
					</Typography>
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2.5 }}>
				<Button onClick={handleClose} disabled={startDraft.isPending}>
					{t("common.cancel")}
				</Button>
				<Button variant="contained" onClick={handleSubmit} disabled={startDraft.isPending || isImporting}>
					{t("presentationDraft.start.submit")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default StartPresentationDraftDialog;
